import React, { useState, useEffect } from 'react';
import {
  Sparkles, Plug, MessageSquare, FileSearch, ShieldCheck, AlertTriangle,
  Copy, Check, ChevronDown, ChevronRight, Wrench, HelpCircle, Boxes,
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import axiosInstance from '../utils/axiosInstance';

const Section = ({ icon: Icon, title, description, children, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Card className="border-border">
      <CardHeader className="cursor-pointer select-none" onClick={() => setOpen((p) => !p)}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary/10 text-primary">
              <Icon className="h-5 w-5" />
            </div>
            <div>
              <CardTitle className="text-lg">{title}</CardTitle>
              {description && <CardDescription className="mt-1">{description}</CardDescription>}
            </div>
          </div>
          <div className="text-muted-foreground">
            {open ? <ChevronDown className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
          </div>
        </div>
      </CardHeader>
      {open && <CardContent>{children}</CardContent>}
    </Card>
  );
};

const Step = ({ n, title, children }) => (
  <div className="flex items-start gap-3">
    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10 text-primary text-xs font-bold border border-primary/20">
      {n}
    </div>
    <div className="min-w-0">
      <p className="font-medium text-sm">{title}</p>
      <div className="text-sm text-muted-foreground mt-0.5">{children}</div>
    </div>
  </div>
);

const Ask = ({ children }) => (
  <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm italic">“{children}”</div>
);

const ClaudeConnector = () => {
  const [conn, setConn] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    axiosInstance.get('api/mcp-connection/')
      .then((r) => setConn(r.data))
      .catch(() => setConn({ error: true }));
  }, []);

  const copy = async () => {
    if (!conn?.url) return;
    try {
      await navigator.clipboard.writeText(conn.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (e) { /* clipboard blocked, the link is selectable anyway */ }
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-6">
      <div className="flex items-center gap-3 mb-2">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Sparkles className="h-6 w-6" />
        </div>
        <div>
          <h1 className="text-2xl font-bold">Shippy in Claude</h1>
          <p className="text-muted-foreground text-sm">
            Connect the Claude app to Shippy and check carrier invoices by asking.
          </p>
        </div>
      </div>

      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="pt-6 text-sm space-y-2">
          <p>
            <strong>What this is for.</strong> A carrier sends an invoice. For every line you want to know
            whether the amount matches what the shipment should have cost us. Shippy already knows that
            number for every order: how it was packed, which carrier carried it, the rate, the fuel and the
            surcharges. Connect Claude to Shippy once, then attach the invoice and ask.
          </p>
          <p className="text-muted-foreground">
            Claude reads the invoice, asks Shippy for the expected cost of each order, and reports the lines
            that do not match. It can only read; nothing in Shippy can be changed from Claude. To get started,
            message Geoffrey for your connector link.
          </p>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">What it can do</CardTitle>
          <CardDescription>Seven things, all read-only</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            {[
              ['Check one order', 'What it should have cost, which carrier, how many parcels, which surcharges, and what the customer paid.'],
              ['Check a whole invoice', 'Attach it and get a verdict per line: matches, overcharged, undercharged, wrong carrier, or no record, plus the totals.'],
              ['Price a parcel', 'Give size, weight and destination: what each carrier should charge for it.'],
              ['Explain a surcharge', 'The carriers, fuel percentages and surcharge rules behind a country.'],
              ['Reconcile a period', 'What we shipped with a carrier to a country between two dates, and the total.'],
              ['Re-pack an order', "Run today's rates on an old order, see the cheapest carrier, or ask what another destination would cost."],
              ['Price a basket', 'Give SKUs and a destination: how it packs, what it costs, per carrier.'],
              ['Read the rate card', 'Every carrier on a country with its weight bands, zone overrides, pickup bands, fuel and surcharge rules.'],
            ].map(([title, body]) => (
              <div key={title}>
                <p className="font-medium">{title}</p>
                <p className="text-muted-foreground text-[13px] mt-0.5">{body}</p>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Section icon={Plug} title="Set it up once" description="About two minutes in the Claude desktop app" defaultOpen>
        <div className="space-y-4">
          <div className="rounded-lg border border-border p-3">
            <div className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Your connector link</div>
            {conn === null && <div className="text-sm text-muted-foreground">Loading…</div>}
            {conn?.error && (
              <div className="text-sm text-red-600 dark:text-red-400">
                Could not load the link. Sign in to Shippy again, or ask Geoffrey for it.
              </div>
            )}
            {conn && conn.configured === false && (
              <div className="text-sm space-y-1">
                <p>
                  <span className="text-amber-700 dark:text-amber-400 font-medium">Not handed out yet.</span>{' '}
                  The link looks like{' '}
                  <code className="text-xs bg-muted px-1.5 py-0.5 rounded">
                    https://api.shippy.lamptwist.be/api/mcp/&lt;key&gt;/
                  </code>{' '}
                  and appears here as soon as the key is set.
                </p>
                <p className="text-muted-foreground text-xs">{conn.note}</p>
              </div>
            )}
            {conn?.url && (
              <div className="flex items-center gap-2 flex-wrap">
                <code className="text-xs bg-muted px-2 py-1 rounded break-all flex-1 min-w-[260px]">{conn.url}</code>
                <button
                  type="button"
                  onClick={copy}
                  className="inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3 py-1.5 text-xs font-medium hover:opacity-90"
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? 'Copied' : 'Copy link'}
                </button>
              </div>
            )}
            <p className="text-xs text-muted-foreground mt-2">
              Treat this link like a password: it carries the key to Shippy's data. Do not post it in a public
              channel.
            </p>
            <p className="text-xs mt-1">
              <strong>No link shown, or no Shippy login?</strong> Send Geoffrey a message and he will give you
              the link directly.
            </p>
          </div>

          <div className="space-y-3">
            <Step n={1} title="Open the Claude desktop app">
              On your computer, not the website. Sign in with your work account.
            </Step>
            <Step n={2} title="Go to Settings, then Connectors">
              Click your name at the bottom left, then Settings, then Connectors.
            </Step>
            <Step n={3} title="Add a custom connector">
              Click <strong>Add custom connector</strong>, paste the link above, and give it the name
              <strong> Shippy</strong>. If you do not have the link, message Geoffrey for it.
            </Step>
            <Step n={4} title="Check it connected">
              Shippy appears in the list with its tools. In a new chat, the attachment menu shows Shippy as
              available.
            </Step>
            <Step n={5} title="Ask your first question">
              Try: “Ask Shippy what order 188614 should have cost to ship.” If it answers with a carrier and an
              amount, you are done.
            </Step>
          </div>
        </div>
      </Section>

      <Section icon={FileSearch} title="Checking an invoice" description="The main job: attach it and ask">
        <div className="space-y-3 text-sm">
          <p>
            Attach the invoice to the chat as a PDF, Excel or CSV. Claude reads the lines itself, then asks
            Shippy for the expected cost of each order and compares them.
          </p>
          <Ask>Here is the DHL invoice for August. Check every line against Shippy and list the ones that
            do not match.</Ask>
          <Ask>This GLS invoice has 120 lines. Which ones are overcharged by more than 5 euro, and what is
            the total difference?</Ask>
          <Ask>Invoice total is 4,312 euro for 210 shipments. Does that match what we shipped with GLS to
            Spain in August?</Ask>
          <p className="text-muted-foreground">
            The invoice needs the order number on each line. If the carrier only gives tracking numbers, send
            the file anyway and say so: Claude will tell you which lines it could not match rather than
            guessing.
          </p>
          <div className="rounded-lg border border-yellow-500/30 bg-yellow-500/5 p-3">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-400 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium">Amounts are excluding VAT</p>
                <p className="text-muted-foreground mt-0.5">
                  Shippy's expected cost is what the carrier should bill us, VAT excluded. If the invoice
                  shows amounts including VAT, say so in the chat so Claude compares like for like.
                </p>
              </div>
            </div>
          </div>
        </div>
      </Section>

      <Section icon={MessageSquare} title="Other things worth asking" description="Beyond invoice checking">
        <div className="space-y-3 text-sm">
          <Ask>What did order 188614 cost us, and which carrier should have shipped it?</Ask>
          <Ask>A 40 by 30 by 20 cm parcel of 3 kg to Madrid: what should each carrier charge?</Ask>
          <Ask>DHL billed an oversize surcharge on a 120 cm parcel to France. Is that a surcharge we know
            about?</Ask>
          <Ask>How many shipments did we send to Poland last month and what did they cost us in total?</Ask>
          <Ask>Compare this quote from a new carrier against what we pay today for parcels to Italy.</Ask>
          <Ask>Show me the GLS bands for Switzerland, and what we pay at 12 kg.</Ask>
          <Ask>Which carriers serve Italy, and do Sicily and Sardinia have their own rates?</Ask>
        </div>
      </Section>

      <Section icon={Boxes} title="Simulations and what-ifs" description="Re-pack an order or a basket with today's rates">
        <div className="space-y-3 text-sm">
          <p>
            Claude can run the real packing engine, the same one behind the simulator page and the checkout:
            it works out the boxes, the parcels and the price, then compares every carrier on that lane.
          </p>
          <Ask>Re-pack order 188621 and tell me which carrier would have been cheapest.</Ask>
          <Ask>What would order 188621 have cost if it went to Berlin instead?</Ask>
          <Ask>Two of SKU HMWL-0220742 and one of HNABP-017340 to Madrid: how does that pack and what does
            it cost per carrier?</Ask>
          <Ask>A customer wants these four items to Lisbon. Price it with each carrier so I can pick.</Ask>
          <p className="text-muted-foreground">
            A simulation uses today's rates and today's box catalogue, so it can differ from what an old
            order actually cost. When the order has a stored analysis, the answer shows both and says so.
            For a basket, give SKUs: anything Claude cannot find is listed instead of being quietly left out
            of the price.
          </p>
        </div>
      </Section>

      <Section icon={HelpCircle} title="Reading the answer" description="What the numbers mean">
        <div className="space-y-3 text-sm">
          <ul className="space-y-2">
            <li>
              <strong>Expected cost</strong> is what the carrier should bill us: their rate, plus fuel, plus
              any surcharge, plus our box. It is <em>not</em> what the customer paid for shipping, which is
              shown separately.
            </li>
            <li>
              <strong>Overcharged / undercharged</strong> compares the invoice line with that expected cost.
              Small differences are ignored by default, one euro or ten percent, because rounding and fuel
              timing move the last cents.
            </li>
            <li>
              <strong>Carrier mismatch</strong> means the invoice came from a different carrier than the one
              Shippy expected to carry the order. Worth a question to the carrier, or to ops.
            </li>
            <li>
              <strong>No record</strong> means Shippy has no analysis for that order number. Usually a typo, a
              very old order, or an order that was never analysed.
            </li>
          </ul>
          <p className="text-muted-foreground">
            Some orders carry a note, and it changes how you read the line: a split shipment covers two
            shipments so expect two invoice lines, a pickup order is priced on the pickup grid, an own-shipping
            order should never be invoiced to us at all, and a forced-carrier order was priced on the carrier
            the customer chose rather than the cheapest.
          </p>
        </div>
      </Section>

      <Section icon={Wrench} title="If something does not work" description="The usual three">
        <div className="space-y-3 text-sm">
          <div>
            <p className="font-medium">The connector will not add, or shows an error</p>
            <p className="text-muted-foreground">
              The link is probably incomplete. It must end with a slash. Copy it again from this page.
            </p>
          </div>
          <div>
            <p className="font-medium">Claude answers without asking Shippy</p>
            <p className="text-muted-foreground">
              Say “ask Shippy” in the question, and check that Shippy is switched on for that chat in the
              attachment menu. An answer invented without the tools will be wrong.
            </p>
          </div>
          <div>
            <p className="font-medium">It says an order is not found</p>
            <p className="text-muted-foreground">
              Orders are analysed within about thirty minutes of being placed, so a very recent order may not
              be there yet. Otherwise check the order number on the invoice line.
            </p>
          </div>
        </div>
      </Section>

      <Section icon={ShieldCheck} title="What Claude can and cannot do" description="Scope and safety">
        <div className="space-y-3 text-sm">
          <p>
            <strong>Read only.</strong> The connector exposes eight read-only tools: one order, a whole
            invoice at once, the price of a parcel on a lane, the carrier and surcharge setup for a country,
            the shipments in a period, two simulations (re-pack an order, or pack a basket of SKUs) and the rate
            card for a country. Nothing can be changed, deleted or booked from Claude: rates are edited in Shippy,
            by a person, on the Shipping Rates page.
          </p>
          <p>
            <strong>What it can see.</strong> Shipping data: order numbers, destination country, packing,
            carriers, costs and what was paid for shipping. Not payment details, and no customer names or
            addresses.
          </p>
          <p className="text-muted-foreground">
            Every call is logged on the Shippy side. If a link is ever shared by accident, tell Geoffrey: the
            key is replaced and all links stop working at once.
          </p>
        </div>
      </Section>
    </div>
  );
};

export default ClaudeConnector;
